import { useState, useEffect } from 'react';
import { useMedplum } from '@medplum/react';
import {
  Consent,
  ConsentProvision,
  DocumentReference,
  ServiceRequest,
} from '@medplum/fhirtypes';
import {
  loadDocumentReference,
  parseConsentToData,
} from '@utils/consent.utils';
import { ConsentData, ConsentStatus } from '@/types/consent.types';
import { EXTERNAL_CS, SYSTEMS } from '@/consts/systems.consts';
import { MedplumClient } from '@medplum/core';

// Export a standalone function for checking consent without loading full data
export async function checkConsentStatus(
  medplum: MedplumClient,
  serviceRequestId: string
): Promise<ConsentProvision['type'] | undefined> {
  try {
    const consent = await medplum.searchOne('Consent', {
      data: `ServiceRequest/${serviceRequestId}`,
    });
    return consent?.provision?.type ?? undefined;
  } catch (error) {
    console.error('Error checking consent:', error);
    return undefined;
  }
}

// Export a function to get consent status (not answered, accepted, denied)
export async function getConsentStatus(
  medplum: MedplumClient,
  serviceRequestId: string
): Promise<ConsentStatus> {
  try {
    const consents = await medplum.searchResources('Consent', {
      data: `ServiceRequest/${serviceRequestId}`,
      _count: 1,
    });

    if (consents.length === 0) {
      return 'not-answered';
    }

    const consent = consents[0];
    // Check the main provision type to determine if it's permit or deny
    const provisionType = consent.provision?.type;

    if (!provisionType) {
      return 'not-answered';
    }

    // Default to not-answered if we can't determine the type
    return provisionType;
  } catch (error) {
    console.error('Error getting consent status:', error);
    return 'not-answered';
  }
}

export function useConsent(serviceRequest: ServiceRequest) {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState<Consent | null>(null);
  const [consentData, setConsentData] = useState<ConsentData | null>(null);
  const [documents, setDocuments] = useState<DocumentReference[]>([]);

  useEffect(() => {
    loadConsent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceRequest.id]);

  const loadConsent = async () => {
    if (!serviceRequest.id) {
      return;
    }

    try {
      setLoading(true);
      // Search for consent related to this service request
      const consents = await medplum.searchResources('Consent', {
        data: `ServiceRequest/${serviceRequest.id}`,
        _count: 1,
      });

      if (consents.length > 0) {
        const consentResource = consents[0];
        setConsent(consentResource);
        const parsedData = parseConsentToData(consentResource);
        setConsentData(parsedData);

        // Load linked documents if sourceReference exists
        const documentsToLoad: DocumentReference[] = [];

        // Handle multiple source references (consent files)
        const sourceRefs = Array.isArray(consentResource.sourceReference)
          ? consentResource.sourceReference
          : [consentResource.sourceReference];

        for (const sourceRef of sourceRefs) {
          const doc = await loadDocumentReference(sourceRef, medplum);
          if (doc) {
            documentsToLoad.push(doc);
          }
        }

        // Load authorization documents with LOINC code 64292-6 related to this ServiceRequest
        try {
          const authDocs = await medplum.searchResources('DocumentReference', {
            type: `${SYSTEMS.LOINC}|${EXTERNAL_CS.LOINC.CODES.AUTHORIZATION_DOCUMENT}`,
            related: `ServiceRequest/${serviceRequest.id}`,
            _count: 10,
          });
          documentsToLoad.push(...authDocs);
        } catch (error) {
          console.error('Error loading authorization documents:', error);
        }

        setDocuments(documentsToLoad);

        // Load RelatedPerson information if performer is a RelatedPerson
        if (
          consentResource.performer?.[0]?.reference?.startsWith(
            'RelatedPerson/'
          )
        ) {
          try {
            const relatedPersonId =
              consentResource.performer[0].reference.split('/')[1];
            const relatedPerson = await medplum.readResource(
              'RelatedPerson',
              relatedPersonId
            );

            // Update consent data with RelatedPerson information
            const updatedConsentData = {
              ...parsedData,
              selectedRelatedPerson: relatedPerson,
            };
            setConsentData(updatedConsentData);
          } catch (error) {
            console.error('Error loading RelatedPerson:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading consent:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasConsent = consent !== null;

  return {
    loading,
    consent,
    consentData,
    documents,
    hasConsent,
    refetch: loadConsent,
  };
}
